import React, { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Badge } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import {
    FaFileInvoice, FaPills, FaSyringe, FaClock, FaFlask, FaXRay, FaCut, FaPaw,
    FaMoneyBillWave, FaPrint, FaStethoscope, FaHeartbeat, FaTrashAlt, FaPlus,
    FaEdit, FaSave, FaCalendarAlt, FaLock, FaWallet, FaCheckCircle,
    FaExclamationCircle, FaDog, FaCat, FaTooth, FaHospitalUser, FaBox,
    FaShoppingCart
} from "react-icons/fa";
import moment from "moment";
import "../../assets/fonts/appointmentInvoice.css";

export default function PetInvoiceComponent({ appointmentId, petId, customerId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingPaymentId, setDeletingPaymentId] = useState(null);
    const [editingPaymentId, setEditingPaymentId] = useState(null);

    const [customerBalance, setCustomerBalance] = useState(null);
    const [showPendingModal, setShowPendingModal] = useState(false);
    const [pendingAppointments, setPendingAppointments] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [showCustomerPaymentModal, setShowCustomerPaymentModal] = useState(false);
    const [selectedAppointmentForPayment, setSelectedAppointmentForPayment] = useState(null);
    const [customerPaymentForm, setCustomerPaymentForm] = useState({ payment_method_id: '', amount: 0, payment_date: getTodayDate() });
    const [modalPayments, setModalPayments] = useState([{ id: null, payment_method_id: "", amount: "", payment_date: getTodayDate(), isEditing: false }]);
    const [paymentMethodMissing, setPaymentMethodMissing] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const location = useLocation();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const hasPermission = (perm) => permissions.includes(perm);
    const canManagePayments = () => hasPermission("Appointment Payments Manage");
    const userRole = localStorage.getItem("role") || "";
    const isAdmin = userRole.toLowerCase() === "admin";

    // Hardcoded Unpaid payment method
    const UNPAID_METHOD_ID = -1; // Use a negative or unique ID that won't conflict with backend
    const UNPAID_METHOD = {
        id: UNPAID_METHOD_ID,
        name: "Unpaid",
        is_unpaid: true
    };

    function getTodayDate() {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }

    const canModifyInvoice = (isFullyPaid) => isFullyPaid ? isAdmin : canManagePayments();

    useEffect(() => {
        if (customerId) fetchCustomerBalance(customerId);
        else { setCustomerBalance(null); setPendingAppointments([]); }
    }, [customerId]);

    useEffect(() => { fetchInvoice(); fetchPaymentMethods(); }, [appointmentId, petId, location]);

    const fetchCustomerBalance = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/appointments/ledger/${custId}`, { headers: { Authorization: `Bearer ${AuthToken}` } });
            setCustomerBalance(response.data);
        } catch (e) { setCustomerBalance(null); } finally { setLoadingBalance(false); }
    };

    const fetchPendingAppointments = async (custId) => {
        setLoadingBalance(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/appointments/pending/${custId}`, { headers: { Authorization: `Bearer ${AuthToken}` } });
            if (response.data.status) setPendingAppointments(response.data.appointments || []);
        } catch (e) { toast.error(t('Failed to fetch pending appointments')); } finally { setLoadingBalance(false); }
    };

    const fetchInvoice = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/pet-invoice/${appointmentId}`, { headers: { Authorization: `Bearer ${AuthToken}` } });

            if (response.data?.success && response.data?.invoice) {
                setInvoiceData(response.data.invoice);
            } else {
                setInvoiceData(null);
            }
        } catch (e) {
            console.error('Error fetching invoice:', e);
            toast.error(t('failed to load invoice'));
            setInvoiceData(null);
        } finally { setLoading(false); }
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoadingMethods(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, { headers: { Authorization: `Bearer ${AuthToken}` } });
            let methods = [];
            if (Array.isArray(response.data)) methods = response.data;
            else if (response.data?.paymentMethods) methods = response.data.paymentMethods;
            else if (response.data?.data) methods = response.data.data;
            else methods = [];

            // Add the hardcoded Unpaid method to the payment methods list
            const methodsWithUnpaid = [...methods, UNPAID_METHOD];
            setPaymentMethods(methodsWithUnpaid);
        } catch (e) {
            // If backend fails, still provide the Unpaid method
            setPaymentMethods([UNPAID_METHOD]);
        } finally {
            setLoadingMethods(false);
        }
    };

    const getTypeIcon = (type) => {
        const t2 = type?.toLowerCase() || '';
        if (t2.includes('consultancy')) return <FaStethoscope />;
        if (t2.includes('vaccine')) return <FaSyringe />;
        if (t2.includes('lab')) return <FaFlask />;
        if (t2.includes('radiology')) return <FaXRay />;
        if (t2.includes('surgery')) return <FaCut />;
        if (t2.includes('deworming')) return <FaPaw />;
        if (t2.includes('prescription')) return <FaPills />;
        if (t2.includes('grooming')) return <FaCut />;
        if (t2.includes('sale')) return <FaMoneyBillWave />;
        if (t2.includes('follow')) return <FaHeartbeat />;
        if (t2.includes('check')) return <FaStethoscope />;
        return <FaHeartbeat />;
    };

    const getChipClass = (type) => {
        const t2 = type?.toLowerCase() || '';
        if (t2.includes('consultancy') || t2.includes('lab') || t2.includes('radiology')) return 'chip-info';
        if (t2.includes('vaccine') || t2.includes('sale')) return 'chip-success';
        if (t2.includes('deworming')) return 'chip-warning';
        if (t2.includes('surgery')) return 'chip-danger';
        if (t2.includes('prescription') || t2.includes('grooming')) return 'chip-purple';
        if (t2.includes('follow')) return 'chip-primary';
        if (t2.includes('check')) return 'chip-secondary';
        return 'chip-dark';
    };

    const calculateGrandTotal = () => {
        if (!invoiceData?.pets) return 0;
        let total = 0;
        invoiceData.pets.forEach(pet => {
            pet.detail.forEach(item => {
                total += parseFloat(item.price) || 0;
            });
        });
        // Add sale items total if any
        if (invoiceData.sale?.details && invoiceData.sale.details.length > 0) {
            invoiceData.sale.details.forEach(saleItem => {
                total += parseFloat(saleItem.total_amount) || 0;
            });
        }
        return total;
    };

    const calculatePaidTotal = () => {
        if (!invoiceData?.payment) return 0;
        // Filter out payments that are marked as unpaid
        return invoiceData.payment
            .filter(p => p.payment_method_id !== UNPAID_METHOD_ID)
            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    };

    const getRemainingAmount = () => calculateGrandTotal() - calculatePaidTotal();

    const getParsedAmount = (v) => { if (!v && v !== 0) return 0; const n = parseFloat(v); return isNaN(n) ? 0 : n; };

    const getModalPaymentsTotal = () => modalPayments.reduce((sum, p) => {
        // Skip payments that are unpaid (amount should be 0 in the total)
        if (parseInt(p.payment_method_id) === UNPAID_METHOD_ID) return sum;
        return (!p.isEditing && p.amount ? sum + getParsedAmount(p.amount) : sum);
    }, 0);

    const getModalRemainingAfterPayments = () => invoiceData ? getRemainingAmount() - getModalPaymentsTotal() : 0;

    const formatAmount = (v) => {
        if (v == null || v === '') return '0';
        const n = typeof v === 'string' ? parseFloat(v) : Number(v);
        if (isNaN(n)) return String(v);
        return Number.isInteger(n) ? n.toString() : n.toFixed(2);
    };

    const formatDate = (d) => {
        if (!d) return 'N/A';
        try { return moment(d).format('DD MMM YYYY'); } catch { return 'N/A'; }
    };

    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        try { return moment(d).format('DD MMM YYYY hh:mm A'); } catch { return 'N/A'; }
    };

    const formatLabel = (text) => {
        if (!text) return '';
        return String(text).replace(/_/g, ' ').replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
    };

    const handlePrint = () => {
        if (!appointmentId) return;
        const w = window.open(`${process.env.REACT_APP_URL}/invoice-print/${appointmentId}`, '_blank');
        if (w) w.focus();
    };

    const handleOpenAddPayment = () => {
        const remaining = getRemainingAmount();
        const isFullyPaid = remaining <= 0;
        if (!canModifyInvoice(isFullyPaid)) { toast.error(isFullyPaid ? t('invoice_is_locked_fully_paid') : t('you_dont_have_permission')); return; }
        setEditingPaymentId(null);
        setModalPayments([{ id: null, payment_method_id: "", amount: remaining > 0 ? String(remaining) : "", payment_date: getTodayDate(), isEditing: false }]);
        setShowPaymentModal(true);
    };

    const handleOpenEditPayment = (payment) => {
        const isFullyPaid = getRemainingAmount() <= 0;
        if (!canModifyInvoice(isFullyPaid)) { toast.error(isFullyPaid ? t('invoice_is_locked_fully_paid') : t('you_dont_have_permission')); return; }
        setEditingPaymentId(payment.id);
        setModalPayments([{ id: payment.id, payment_method_id: payment.payment_method_id, amount: payment.amount, payment_date: payment.payment_date ? moment(payment.payment_date).format('YYYY-MM-DD') : getTodayDate(), isEditing: true }]);
        setShowPaymentModal(true);
    };

    const handleAddPaymentRow = () => {
        const remaining = getModalRemainingAfterPayments();
        if (remaining <= 0) { toast.warning(t('no_remaining_amount_to_add')); return; }
        setModalPayments([...modalPayments, { id: null, payment_method_id: "", amount: String(remaining), payment_date: getTodayDate(), isEditing: false }]);
    };

    const handleRemovePaymentRow = (index) => {
        if (modalPayments.length === 1) { toast.warning(t('at_least_one_payment_required')); return; }
        setModalPayments(modalPayments.filter((_, i) => i !== index));
    };

    const handlePaymentRowChange = (index, field, value) => {
        const newPayments = [...modalPayments];

        if (field === 'payment_method_id') {
            setPaymentMethodMissing(false);
            newPayments[index][field] = value;
            // If the selected method is unpaid, set amount to null and disable amount field
            if (parseInt(value) === UNPAID_METHOD_ID) {
                newPayments[index].amount = null; // Set to null
                newPayments[index].isUnpaid = true;
            } else {
                newPayments[index].isUnpaid = false;
                // Reset amount to remaining if it was null
                if (newPayments[index].amount === null) {
                    const remaining = getModalRemainingAfterPayments();
                    newPayments[index].amount = remaining > 0 ? String(remaining) : "";
                }
            }
        } else if (field === 'amount') {
            // Don't allow amount change if unpaid method is selected
            if (parseInt(newPayments[index].payment_method_id) === UNPAID_METHOD_ID) {
                return;
            }
            newPayments[index][field] = value;
            const otherTotal = newPayments.reduce((s, p, i) => {
                if (i !== index && !p.isEditing && parseInt(p.payment_method_id) !== UNPAID_METHOD_ID) {
                    return s + getParsedAmount(p.amount);
                }
                return s;
            }, 0);
            const originalAmount = newPayments[index].isEditing
                ? getParsedAmount(invoiceData?.payment?.find(p => p.id === newPayments[index].id)?.amount)
                : 0;
            const maxAllowed = Math.max(0, getRemainingAmount() + originalAmount - otherTotal);
            if (getParsedAmount(value) > maxAllowed) {
                newPayments[index][field] = String(maxAllowed);
                toast.warning(`${t('Amount cannot exceed remaining balance')}: ${formatAmount(maxAllowed)}`);
            }
        } else {
            newPayments[index][field] = value;
        }

        setModalPayments(newPayments);
    };

    const handleSavePayments = async () => {
        // Every payment row needs a payment method before it can be saved
        if (modalPayments.some(p => !p.payment_method_id)) {
            setPaymentMethodMissing(true);
            toast.warning(t('Please select payment method'));
            return;
        }

        // Filter out unpaid payments (they will be sent with amount: null)
        const validPayments = modalPayments.filter(p => p.payment_method_id && parseInt(p.payment_method_id) !== UNPAID_METHOD_ID && p.amount && getParsedAmount(p.amount) > 0);
        const unpaidPayments = modalPayments.filter(p => parseInt(p.payment_method_id) === UNPAID_METHOD_ID);

        const grandTotal = calculateGrandTotal();
        const currentPaid = calculatePaidTotal();
        let finalTotal = currentPaid;

        // Calculate total for regular payments
        validPayments.forEach(p => {
            if (p.isEditing && p.id) {
                const orig = invoiceData?.payment?.find(pay => pay.id === p.id);
                finalTotal = finalTotal - getParsedAmount(orig?.amount || 0) + getParsedAmount(p.amount);
            }
            else finalTotal += getParsedAmount(p.amount);
        });

        // Unpaid payments don't affect the total paid amount
        if (finalTotal > grandTotal) {
            toast.error(t('total_payment_amount_exceeds_grand_total'));
            return;
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");

            // Prepare payments array - regular payments with amounts
            const paymentsArray = validPayments.map(p => ({
                ...(p.isEditing && p.id ? { id: p.id } : {}),
                payment_method_id: parseInt(p.payment_method_id),
                amount: getParsedAmount(p.amount),
                payment_date: p.payment_date
            }));

            // For unpaid payments, send amount as null
            unpaidPayments.forEach(up => {
                paymentsArray.push({
                    ...(up.isEditing && up.id ? { id: up.id } : {}),
                    payment_method_id: UNPAID_METHOD_ID, // Send the unpaid method ID
                    amount: null, // Send null for unpaid payments
                    payment_date: up.payment_date,
                    is_unpaid: true // Add flag to indicate this is an unpaid entry
                });
            });

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/pet-invoice-add-payment/${appointmentId}`,
                { payments: paymentsArray },
                { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data.success) {
                toast.success(t('payments_processed_successfully'));
                setShowPaymentModal(false);
                setEditingPaymentId(null);
                setModalPayments([{ id: null, payment_method_id: "", amount: "", payment_date: getTodayDate(), isEditing: false }]);
                await fetchInvoice();
                if (customerId) { await fetchCustomerBalance(customerId); if (showPendingModal) await fetchPendingAppointments(customerId); }
            } else toast.error(response.data.message || t('payment_failed'));
        } catch (e) {
            console.error('Payment error:', e);
            toast.error(e.response?.data?.message || t('payment_failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePayment = async (paymentId) => {
        const isFullyPaid = getRemainingAmount() <= 0;
        if (!canModifyInvoice(isFullyPaid)) { toast.error(isFullyPaid ? t('invoice_is_locked_fully_paid') : t('you_dont_have_permission')); return; }
        const result = await Swal.fire({ title: t('confirm_delete'), text: t('are_you_sure_delete_payment'), icon: 'warning', showCancelButton: true, confirmButtonText: t('delete'), cancelButtonText: t('cancel'), reverseButtons: true });
        if (!result.isConfirmed) return;
        setDeletingPaymentId(paymentId);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/payment-destroy/${paymentId}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } });
            if (response.data.message === 'Payment deleted successfully') {
                toast.success(t('payment_deleted_successfully'));
                await fetchInvoice();
                if (customerId) { await fetchCustomerBalance(customerId); if (showPendingModal) await fetchPendingAppointments(customerId); }
            } else toast.error(response.data.message || t('failed_to_delete_payment'));
        } catch (e) { toast.error(e.response?.data?.message || t('failed_to_delete_payment')); } finally { setDeletingPaymentId(null); }
    };

    const handleShowPendingAppointments = async () => {
        if (!customerId) { toast.warning(t('No customer selected')); return; }
        await fetchPendingAppointments(customerId);
        setShowPendingModal(true);
    };

    const handleCustomerPaymentSubmit = async () => {
        if (!customerPaymentForm.payment_method_id) { toast.warning(t('Please select payment method')); return; }
        if (customerPaymentForm.amount <= 0) { toast.warning(t('Amount must be greater than 0')); return; }
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/appointments/allocate-payment`, { customer_id: customerId, ...customerPaymentForm }, { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } });
            if (response.data.status) {
                toast.success(t('Payment allocated successfully'));
                setShowCustomerPaymentModal(false);
                await fetchCustomerBalance(customerId);
                if (showPendingModal) await fetchPendingAppointments(customerId);
                await fetchInvoice();
            }
        } catch (e) { toast.error(e.response?.data?.message || t('Payment failed')); }
    };

    if (loading) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', color: '#9ca3af' }}>
            <Spinner animation="border" style={{ color: '#111827', width: 36, height: 36, borderWidth: 3 }} />
            <p style={{ marginTop: 16, fontSize: 14 }}>{t('loading invoice')}</p>
        </div>
    );

    if (!invoiceData || !invoiceData.pets || invoiceData.pets.length === 0) return (
        <div className="inv-empty">
            <FaFileInvoice size={42} style={{ color: '#d1d5db' }} />
            <p>{t('no invoice found')}</p>
        </div>
    );

    const grandTotal = calculateGrandTotal();
    const paidTotal = calculatePaidTotal();
    const remainingAmount = getRemainingAmount();
    const isFullyPaid = remainingAmount <= 0;
    const canModify = canModifyInvoice(isFullyPaid);
    const hasSales = invoiceData.sale?.details && invoiceData.sale.details.length > 0;

    return (
        <>
            <div className="inv-root">

                {/* Customer Balance Banner */}
                {customerBalance && customerBalance.total_balance > 0 && (
                    <div className="inv-balance-banner">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div className="balance-icon-wrap"><FaWallet size={20} color="#ffc107" /></div>
                            <div>
                                <div className="balance-label">{t('Customer Balance')}</div>
                                <div className="balance-value">{parseFloat(customerBalance.total_balance).toLocaleString()}</div>
                            </div>
                        </div>
                        {customerBalance.total_due != null && (
                            <div className="stat-chip">
                                <div className="stat-chip-label">{t('Total Due')}</div>
                                <div className="stat-chip-value">{parseFloat(customerBalance.total_due).toLocaleString()}</div>
                            </div>
                        )}
                        <button className="btn-balance-action" onClick={handleShowPendingAppointments}>
                            <FaFileInvoice size={13} /> {t('View Pending Appointments')}
                        </button>
                    </div>
                )}

                {/* Invoice Card */}
                <div className="inv-card">
                    {/* Header */}
                    <div className="inv-header" style={{ cursor: 'default' }}>
                        <div className="inv-header-left">
                           
                            {isFullyPaid
                                ? <span className="inv-badge-paid"><FaCheckCircle size={10} /> {t('Paid')}</span>
                                : <span className="inv-badge-pending"><FaExclamationCircle size={10} /> {t('Pending')}</span>}
                        </div>
                        <div className="inv-header-right">
                            {!isFullyPaid && canManagePayments() && (
                                <button className="btn-inv-success" onClick={handleOpenAddPayment}>
                                    <FaMoneyBillWave size={12} /> {t('make payment')}
                                </button>
                            )}
                            <button className="btn-inv-success" onClick={handlePrint}>
                                <FaPrint size={12} /> {t('Print')}
                            </button>
                            <span className="inv-total-badge">{formatAmount(grandTotal)}</span>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="inv-body">

                        {/* Items Table - Grouped by Pet */}
                        <div style={{ marginBottom: 28 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <div className="inv-section-label">{t('invoice_details')}</div>
                                {isFullyPaid && (
                                    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20, ...(isAdmin ? { background: '#eff6ff', color: '#2563eb' } : { background: '#f3f4f6', color: '#374151' }) }}>
                                        {isAdmin ? <><FaEdit size={10} style={{ marginRight: 5 }} />{t('Admin - Editable')}</> : <><FaLock size={10} style={{ marginRight: 5 }} />{t('Locked')}</>}
                                    </span>
                                )}
                            </div>

                            {/* Loop through each pet */}
                            {invoiceData.pets.length > 0 && invoiceData.pets.map((pet, petIndex) => (
                                pet.detail.length > 0 && (

                                    <div key={petIndex} style={{ marginBottom: 32 }}>
                                        {/* Pet Name Header */}
                                        <div style={{
                                            background: '#f3f4f6',
                                            padding: '10px 16px',
                                            borderRadius: '8px 8px 0 0',
                                            borderBottom: '2px solid #e5e7eb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}>
                                            <FaPaw size={16} color="#6b7280" />
                                            <strong style={{ color: '#374151', fontSize: 15 }}>{t('Pet')}: {pet.pet_name}</strong>
                                            <Badge bg="secondary" style={{ marginLeft: 'auto' }}>
                                                {pet.detail.length} {t('item(s)')}
                                            </Badge>
                                        </div>

                                        <table className="inv-table" style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '5%' }}>#</th>
                                                    <th style={{ width: '20%' }}>{t('Type')}</th>
                                                    <th style={{ width: '45%' }}>{t('Name')}</th>
                                                    <th style={{ width: '15%' }}>{t('Date')}</th>
                                                    <th className="text-right" style={{ width: '15%' }}>{t('Amount')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pet.detail.map((item, idx) => (
                                                    <tr key={item.id}>
                                                        <td style={{ color: '#9ca3af', fontSize: 13 }}>{idx + 1}</td>
                                                        <td>
                                                            <span className={`type-chip ${getChipClass(item.type)}`}>
                                                                {getTypeIcon(item.type)}
                                                                {formatLabel(item.type)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span style={{ fontWeight: 500 }}>{formatLabel(item.name)}</span>
                                                        </td>
                                                        <td style={{ fontSize: 12, color: '#6b7280' }}>
                                                            {formatDateTime(item.date)}
                                                        </td>
                                                        <td className="text-right" style={{ fontWeight: 700 }}>
                                                            {formatAmount(item.price)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            ))}

                            {/* Sale Items Section (if any) */}
                            {hasSales && (
                                <div style={{ marginBottom: 32 }}>
                                    <div style={{
                                        background: '#f3f4f6',
                                        padding: '10px 16px',
                                        borderRadius: '8px 8px 0 0',
                                        borderBottom: '2px solid #e5e7eb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10
                                    }}>
                                        <FaMoneyBillWave size={16} color="#059669" />
                                        <strong style={{ color: '#374151', fontSize: 15 }}>{t('Products / Sale Items')}</strong>
                                        <Badge bg="success" style={{ marginLeft: 'auto' }}>
                                            {invoiceData.sale.details.length} {t('item(s)')}
                                        </Badge>
                                    </div>

                                    <table className="inv-table" style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden', width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '5%' }}>#</th>
                                                <th style={{ width: '20%' }}>{t('Type')}</th>
                                                <th style={{ width: '40%' }}>{t('Name')}</th>
                                                <th className="text-center" style={{ width: '15%' }}>{t('Quantity')}</th>
                                                <th className="text-right" style={{ width: '20%' }}>{t('Amount')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoiceData.sale.details.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ color: '#9ca3af', fontSize: 13 }}>{idx + 1}</td>
                                                    <td>
                                                        <span className={`type-chip chip-success`}>
                                                            {getTypeIcon('Sale')}
                                                            {t('Product Sale')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span style={{ fontWeight: 500 }}>{formatLabel(item.name)}</span>
                                                    </td>
                                                    <td className="text-center" style={{ fontSize: 13, fontWeight: 500 }}>
                                                        x{item.qty || 1}
                                                    </td>
                                                    <td className="text-right" style={{ fontWeight: 700 }}>
                                                        {formatAmount(item.total_amount || item.price)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </div>

                        <hr className="inv-divider" />

                        {/* Bottom Section: Payment History + Summary */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>

                            {/* Payment History */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <div className="inv-section-label">{t('payment history')}</div>
                                    {!isFullyPaid && canManagePayments() && (
                                        <button className="btn-inv-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={handleOpenAddPayment}>
                                            <FaPlus size={10} /> {t('add payment')}
                                        </button>
                                    )}
                                </div>

                                {invoiceData.payment && invoiceData.payment.length > 0 ? (
                                    <>
                                        <table className="inv-table" style={{ marginBottom: 0 }}>
                                            <thead>
                                                <tr>
                                                    <th>{t('Date')}</th>
                                                    <th>{t('Method')}</th>
                                                    <th className="text-right">{t('Amount')}</th>
                                                    {canModify && <th className="text-center" style={{ width: 90 }}>{t('Actions')}</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoiceData.payment.map((payment, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ fontSize: 13, color: '#6b7280' }}>
                                                            <FaCalendarAlt size={10} style={{ marginRight: 6, color: '#d1d5db' }} />
                                                            {formatDate(payment.payment_date)}
                                                        </td>
                                                        <td>
                                                            <span className="pay-method">{payment.method?.name || 'N/A'}</span>
                                                        </td>
                                                        <td className="text-right" style={{ fontWeight: 700, color: payment.payment_method_id === UNPAID_METHOD_ID ? '#9ca3af' : '#059669' }}>
                                                            {payment.payment_method_id === UNPAID_METHOD_ID ? 'NULL' : formatAmount(payment.amount)}
                                                        </td>
                                                        {canModify && (
                                                            <td className="text-center">
                                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                                    <button className="pay-action-btn primary" onClick={() => handleOpenEditPayment(payment)} title={t('edit payment')}>
                                                                        <FaEdit size={12} color="#6b7280" />
                                                                    </button>
                                                                    <button className="pay-action-btn danger" onClick={() => handleDeletePayment(payment.id)} disabled={deletingPaymentId === payment.id} title={t('delete payment')}>
                                                                        {deletingPaymentId === payment.id ? <Spinner animation="border" size="sm" /> : <FaTrashAlt size={12} color="#6b7280" />}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan={canModify ? 2 : 1} style={{ fontWeight: 700, color: '#374151' }}>{t('Total Paid')}</td>
                                                    <td className="text-right" style={{ fontWeight: 700, color: '#059669' }}>{formatAmount(paidTotal)}</td>
                                                    {canModify && <td />}
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: 14 }}>
                                        {t('no payments recorded')}
                                    </div>
                                )}
                            </div>

                            {/* Summary */}
                            <div className="inv-summary">
                                <div className="inv-section-label">{t('Summary')}</div>
                                <div className="inv-summary-row">
                                    <span className="inv-summary-label">{t('sub total')}</span>
                                    <span className="inv-summary-value">{formatAmount(grandTotal)}</span>
                                </div>
                                <div className="inv-summary-row">
                                    <span className="inv-summary-label">{t('total paid')}</span>
                                    <span className="inv-summary-value green">{formatAmount(paidTotal)}</span>
                                </div>
                                <div className="inv-summary-row total">
                                    <span>{t('Pending Amount')}</span>
                                    <span className="inv-summary-value red">{formatAmount(remainingAmount)}</span>
                                </div>
                                <div style={{ marginTop: 16 }}>
                                    {isFullyPaid ? (
                                        <div style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: 10, padding: '10px 16px', textAlign: 'center', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                            <FaCheckCircle size={14} /> {t('Fully Paid')}
                                        </div>
                                    ) : (
                                        <div style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', textAlign: 'center', fontSize: 13, fontWeight: 700 }}>
                                            {t('Pending Payment')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Locked Footer */}
                        {isFullyPaid && (
                            <div style={{ marginTop: 24, marginLeft: -28, marginRight: -28, marginBottom: -28 }}>
                                <div className={`inv-status-bar ${isAdmin ? 'admin' : 'paid'}`}>
                                    {isAdmin ? <><FaEdit size={12} /> {t('Admin: Fully paid invoice - Edit access granted')}</> : <><FaLock size={12} /> {t('This invoice is locked as it is fully paid. Only admin can modify.')}</>}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Payment Modal */}
                <Modal show={showPaymentModal} onHide={() => { setShowPaymentModal(false); setEditingPaymentId(null); setModalPayments([{ id: null, payment_method_id: "", amount: "", payment_date: getTodayDate(), isEditing: false }]); }} size="lg" centered>
                    <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '20px 24px' }}>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: '#111827', fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <FaMoneyBillWave color="#059669" /> {editingPaymentId ? t('Edit Payments') : t('Add Payments')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: 24 }}>
                        {/* Balance Info */}
                        <div className="inv-balance-info-card">
                            <div className="info-card-row" style={{ marginBottom: 12 }}>
                                <div><div className="info-label">{t('total amount')}</div><div className="info-value">{formatAmount(grandTotal)}</div></div>
                                <div style={{ textAlign: 'right' }}><div className="info-label">{t('remaining balance')}</div><div className="info-value red">{formatAmount(remainingAmount)}</div></div>
                            </div>
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                                <div className="info-card-row">
                                    <div><div className="info-label">{t('total paid')}</div><div className="info-value green">{formatAmount(paidTotal)}</div></div>
                                    <div style={{ textAlign: 'right' }}><div className="info-label">{t('new payments total')}</div><div className="info-value blue">{formatAmount(getModalPaymentsTotal())}</div></div>
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 12 }}>
                                <div className="info-label">{t('remaining after new payments')}</div>
                                <div className="info-value red">{formatAmount(getModalRemainingAfterPayments())}</div>
                            </div>
                        </div>

                        {/* Payment Rows */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('payment details')}</div>
                            <button className="btn-inv-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={handleAddPaymentRow}>
                                <FaPlus size={10} /> {t('add more payment')}
                            </button>
                        </div>

                        {modalPayments.map((payment, index) => {
                            const selectedIds = modalPayments.filter((_, i) => i !== index && payment.payment_method_id).map(p => p.payment_method_id).filter(Boolean);
                            const availableMethods = paymentMethods.filter(m => !selectedIds.includes(m.id) || m.id === payment.payment_method_id);
                            const isUnpaidSelected = parseInt(payment.payment_method_id) === UNPAID_METHOD_ID;

                            return (
                                <div key={index} className={`inv-payment-row-card ${payment.isEditing ? 'editing' : ''}`}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                                        <div>
                                            <div className="inv-modal-label">{t('payment method')} <span style={{ color: '#dc2626' }}>*</span></div>
                                            <select
                                                className="inv-modal-input"
                                                style={paymentMethodMissing && !payment.payment_method_id ? { borderColor: '#dc2626' } : undefined}
                                                value={payment.payment_method_id || ''}
                                                onChange={e => handlePaymentRowChange(index, 'payment_method_id', e.target.value)}
                                                disabled={loadingMethods}
                                            >
                                                <option value="">{t('select')}</option>
                                                {availableMethods.map(m => (
                                                    <option key={m.id} value={m.id}>
                                                        {m.name} 
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <div className="inv-modal-label">{t('amount')}</div>
                                            <input
                                                className="inv-modal-input"
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                value={payment.amount === null ? '' : (payment.amount ?? '')}
                                                onChange={e => handlePaymentRowChange(index, 'amount', e.target.value)}
                                                placeholder={isUnpaidSelected ? "NULL" : "0.00"}
                                                disabled={isUnpaidSelected}
                                                style={isUnpaidSelected ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                            />
                                        </div>
                                        <div>
                                            <div className="inv-modal-label">{t('payment date')}</div>
                                            <input className="inv-modal-input" type="date" value={payment.payment_date} onChange={e => handlePaymentRowChange(index, 'payment_date', e.target.value)} />
                                        </div>
                                        <button onClick={() => handleRemovePaymentRow(index)} disabled={modalPayments.length === 1} style={{ width: 36, height: 36, border: '1px solid #fca5a5', borderRadius: 8, background: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: modalPayments.length === 1 ? 0.5 : 1 }}>
                                            <FaTrashAlt size={12} color="#dc2626" />
                                        </button>
                                    </div>
                                    {payment.isEditing && <div style={{ fontSize: 11, color: '#92400e', marginTop: 8, display: 'flex', alignItems: 'center', gap: 5 }}><FaEdit size={9} /> {t('editing existing payment')}</div>}
                                </div>
                            );
                        })}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '16px 24px', gap: 10 }}>
                        <button className="btn-inv-ghost" onClick={() => { setShowPaymentModal(false); setEditingPaymentId(null); setModalPayments([{ id: null, payment_method_id: "", amount: "", payment_date: getTodayDate(), isEditing: false }]); }}>
                            {t('cancel')}
                        </button>
                        <button className="btn-inv-success" onClick={handleSavePayments} disabled={submitting}>
                            {submitting ? <><Spinner animation="border" size="sm" style={{ marginRight: 8 }} /> {t('processing')}</> : <><FaSave size={12} /> {t('save payments')}</>}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Customer Payment Modal */}
                <Modal show={showCustomerPaymentModal} onHide={() => setShowCustomerPaymentModal(false)} centered>
                    <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '20px 24px' }}>
                        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>{t('Make Payment')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: 24 }}>
                        {selectedAppointmentForPayment && (
                            <>
                                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
                                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{t('Appointment #')}</div>
                                    <div style={{  fontWeight: 600, fontSize: 15 }}>{selectedAppointmentForPayment.appointment_number}</div>
                                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 10, marginBottom: 4 }}>{t('Due Amount')}</div>
                                    <div style={{  fontWeight: 700, fontSize: 20, color: '#dc2626' }}>{parseFloat(selectedAppointmentForPayment.due).toLocaleString()}</div>
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <div className="inv-modal-label">{t('Payment Method')} <span style={{ color: '#dc2626' }}>*</span></div>
                                    <select className="inv-modal-input" value={customerPaymentForm.payment_method_id} onChange={e => setCustomerPaymentForm({ ...customerPaymentForm, payment_method_id: e.target.value })}>
                                        <option value="">{t('Select Payment Method')}</option>
                                        {paymentMethods.map(pm => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                                    </select>
                                </div>
                                <div style={{ marginBottom: 16 }}>
                                    <div className="inv-modal-label">{t('Amount')} <span style={{ color: '#dc2626' }}>*</span></div>
                                    <input className="inv-modal-input" type="number" step="0.01" min="0.01" max={selectedAppointmentForPayment.due} value={customerPaymentForm.amount ?? ''} onChange={e => setCustomerPaymentForm({ ...customerPaymentForm, amount: parseFloat(e.target.value) || 0 })} />
                                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{t('Max amount')}: {parseFloat(selectedAppointmentForPayment.due)}</div>
                                </div>
                                <div>
                                    <div className="inv-modal-label">{t('Payment Date')} <span style={{ color: '#dc2626' }}>*</span></div>
                                    <input className="inv-modal-input" type="date" value={customerPaymentForm.payment_date} onChange={e => setCustomerPaymentForm({ ...customerPaymentForm, payment_date: e.target.value })} />
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '16px 24px', gap: 10 }}>
                        <button className="btn-inv-ghost" onClick={() => setShowCustomerPaymentModal(false)}>{t('Cancel')}</button>
                        <button className="btn-inv-success" onClick={handleCustomerPaymentSubmit}><FaMoneyBillWave size={12} /> {t('Pay Now')}</button>
                    </Modal.Footer>
                </Modal>

                {/* Pending Appointments Modal */}
                <Modal show={showPendingModal} onHide={() => setShowPendingModal(false)} size="xl" centered>
                    <Modal.Header closeButton style={{ background: '#111827', border: 'none', padding: '20px 24px' }}>
                        <Modal.Title style={{ color: '#fff', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <FaFileInvoice color="#ffc107" /> {t('Pending Appointments')}
                            {pendingAppointments.length > 0 && (
                                <span style={{ background: 'rgba(255,193,7,0.2)', color: '#ffc107', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>{pendingAppointments.length} {t('pending')}</span>
                            )}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: 0, background: '#f9fafb' }}>
                        {loadingBalance ? (
                            <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                                <Spinner animation="border" style={{ color: '#111827' }} />
                                <p style={{ marginTop: 12, color: '#9ca3af' }}>{t('Loading...')}</p>
                            </div>
                        ) : pendingAppointments.length === 0 ? (
                            <div className="inv-empty"><FaFileInvoice size={40} style={{ color: '#d1d5db' }} /><p>{t('No pending appointments found')}</p></div>
                        ) : (
                            <div style={{ padding: 24 }}>
                                <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
                                    {[
                                        { label: t('Total Due'), value: pendingAppointments.reduce((s, a) => s + parseFloat(a.due || 0), 0), color: '#dc2626' },
                                        { label: t('Total Paid'), value: pendingAppointments.reduce((s, a) => s + parseFloat(a.paid || 0), 0), color: '#059669' },
                                        { label: t('Appointments'), value: pendingAppointments.length, color: '#d97706' },
                                    ].map((stat, i) => (
                                        <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 20px' }}>
                                            <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 4 }}>{stat.label}</div>
                                            <div style={{  fontSize: 20, fontWeight: 700, color: stat.color }}>{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                                    <table className="pending-table">
                                        <thead>
                                            <tr>
                                                <th>{t('Appointment #')}</th>
                                                <th>{t('Date')}</th>
                                                <th style={{ textAlign: 'right' }}>{t('Total')}</th>
                                                <th style={{ textAlign: 'right' }}>{t('Paid')}</th>
                                                <th style={{ textAlign: 'right' }}>{t('Due')}</th>
                                                <th style={{ textAlign: 'center' }}>{t('Status')}</th>
                                                <th style={{ textAlign: 'center' }}>{t('Action')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingAppointments.map((appointment, idx) => (
                                                <tr key={idx}>
                                                    <td style={{  fontSize: 13, fontWeight: 500 }}>
                                                        <FaCalendarAlt size={11} style={{ marginRight: 6, color: '#d1d5db' }} />
                                                        {appointment.appointment_number}
                                                    </td>
                                                    <td style={{ fontSize: 13, color: '#6b7280' }}>{moment(appointment.appointment_date).format('DD MMM, hh:mm A')}</td>
                                                    <td style={{ textAlign: 'right',  fontWeight: 600 }}>{parseFloat(appointment.total).toLocaleString()}</td>
                                                    <td style={{ textAlign: 'right',  fontWeight: 600, color: '#059669' }}>{parseFloat(appointment.paid).toLocaleString()}</td>
                                                    <td style={{ textAlign: 'right',  fontWeight: 700, color: '#dc2626' }}>{parseFloat(appointment.due).toLocaleString()}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, ...(appointment.status === 'partial' ? { background: '#fffbeb', color: '#d97706' } : { background: '#fef2f2', color: '#dc2626' }) }}>
                                                            {appointment.status === 'partial' ? t('Partial') : t('Unpaid')}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button className="btn-inv-primary" style={{ fontSize: 12, padding: '6px 14px' }}
                                                            onClick={() => { setShowPendingModal(false); setSelectedAppointmentForPayment(appointment); setCustomerPaymentForm({ payment_method_id: '', amount: appointment.due, payment_date: getTodayDate() }); setShowCustomerPaymentModal(true); }}
                                                            disabled={!canManagePayments()}>
                                                            <FaMoneyBillWave size={11} /> {t('Pay Now')}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer style={{ background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '14px 24px' }}>
                        <button className="btn-inv-ghost" onClick={() => setShowPendingModal(false)}>{t('Close')}</button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
}